import { Icon } from '@iconify/react';
import dayjs from 'dayjs';
import React, { useState } from 'react';
import { ICreateInvoiceData, ICustomer } from '../../../../@types';
import { CloseIcon } from '../../../../assets/icons';
import { InfoCircle } from '../../../../assets/icons/InfoCircle';
import PlusBoxIcon from '../../../../assets/icons/PlusBoxIcon';
import { PAYMENT_CURRENCY_LIST } from '../../../../constants';
import { useCustomerSearch } from '../../../../hooks';
import { InvoiceValidationErrors } from '../../../../modules/invoice/invoice';
import { InvoiceErrors } from '../../../../modules/validateInvoiceForm';
import { SearchInput } from '../../../common';
import { ImportantCard } from '../../../common/TooltipCards/ImportantCard';
import { MiniCard } from '../../../common/TooltipCards/miniCards';
import { DropdownComponent, InputComponent } from '../../../core/Forms';
import { CreateCustomer } from '../../../customers';

const dropDownCurrency = PAYMENT_CURRENCY_LIST;
type ValidationFunction = () => boolean;

interface InvoiceBasicInfoProps {
  handleInputChange: (event: { target: { name: any; value: any } }) => void;
  validationResults: InvoiceErrors;
  formData: ICreateInvoiceData;
  setFormData: React.Dispatch<React.SetStateAction<ICreateInvoiceData>>;
  handleDropDownChange: (name: string, value: string) => void;
  setValidationResults: React.Dispatch<React.SetStateAction<InvoiceErrors>>;
  handleSetCustomer: (selectedCustomer: ICustomer | null) => void;
  step1Validated?: boolean;
  validateCurrentStep?: ValidationFunction;
  invoiceNo?: string;
  showRateInput: boolean;
  customer: ICustomer | null;
  selectedCurrencyLabel: string;
}

const InvoiceBasicInfo: React.FC<InvoiceBasicInfoProps> = ({
  handleInputChange,
  validationResults,
  formData,
  handleDropDownChange,
  setValidationResults,
  step1Validated,
  validateCurrentStep,
  setFormData,
  invoiceNo,
  showRateInput,
  handleSetCustomer,
  customer,
  selectedCurrencyLabel,
}) => {
  const [showNewCustomerModal, setShowNewCustomerModal] = React.useState<boolean>(false);
  const [isStepValid, setIsStepValid] = useState(true);
  const [isMiniCardCustomerVisible, setIsMiniCardCustomerVisible] = useState(false);
  const [isMiniCardCurrencyVisible, setIsMiniCardCurrencyVisible] = useState(false);

  const { data: customers, isSearching, handleSearch: handleCustomerSearch } = useCustomerSearch();

  const [draftValidationResults, setDraftValidationResults] = React.useState<InvoiceValidationErrors>({
    customerId: undefined,
    invoiceNumber: undefined,
    invoiceDueAt: '',
    description: undefined,
    status: undefined,
    issueDate: undefined,
    additionalInformation: undefined,
    shippingCharge: undefined,
    discount: undefined,
    tax: undefined,
    isReoccurring: undefined,
    isReoccurringStartDate: undefined,
    isReoccurringEndDate: undefined,
    reoccurringGroupName: undefined,
    task: undefined,
    inventories: undefined,
    currency: undefined,
    exchangeRate: undefined,
  });

  const handleValidateStep = () => {
    if (validateCurrentStep) {
      const isValid = validateCurrentStep();
      setIsStepValid(isValid);
    }
  };

  const handleInfoCircleHover = () => {
    setIsMiniCardCustomerVisible(true);
  };

  const handleInfoCircleMouseLeave = () => {
    setIsMiniCardCustomerVisible(false);
  };

  const handleInfoCircleCurrencyHover = () => {
    setIsMiniCardCurrencyVisible(true);
  };

  const handleInfoCircleCrrencyMouseLeave = () => {
    setIsMiniCardCurrencyVisible(false);
  };

  return (
    <div className=" ">
      <h2 className="text-center xl:text-[24px] mt-[1em] mb-[5px] lg:mb-0 md:mb-0">Please fill in the required information needed to begin.</h2>
      <form className="flex justify-center">
        <div className="">
          <div className=" lg:grid lg:grid-cols-2 md:grid md:grid-cols-2 xl:gap-[24px] biglg:gap-[22px] lg:gap-[36px] bigmd:mt-[32px] md:mt-[32px] xl:mt-[32px] biglg:mt-[32px] lg:mt-[32px]">
            <div className="ml-[3em] lg:ml-0 xl:ml-0 2xl:ml-0 biglg:ml-0 bigmd:ml-0 md:ml-0 mt-[32px] lg:mt-[-5px] xl:mt-0 2xl:mt-0 biglg:mt-0 bigmd:mt-0 md:mt-[-4px]">
              <label
                htmlFor="customer"
                className=" inline-flex  text-[12px] xl:text-[13px]  ml-[-3.4em] md:ml-[-2em] lg:ml-[2.9em] xl:ml-[8.9em] 375:ml-[-3.8em] 360:ml-[-3.8em]"
                style={{ whiteSpace: 'nowrap' }}
              >
                Customer Name
                <div
                  className="relative hidden lg:block"
                  onMouseEnter={handleInfoCircleHover}
                  onMouseLeave={handleInfoCircleMouseLeave}
                >
                  {isMiniCardCustomerVisible ? (
                    <div className="absolute z-10 mt-[6em] ml-[-9em]">
                      <MiniCard
                        text1="Click New Customer to add a new customer"
                        text2=" by filling the required information in the form."
                        icon={<InfoCircle />}
                      />
                    </div>
                  ) : (
                    <div className='mt-[0.3em]'>
                    <InfoCircle />
                    </div>
                  )}
                </div>
                <span className="text-red-500 ml-[14em] xl:ml-[19.1em] lg:ml-[12.5em] biglg:ml-[12.5em] md:ml-[12.2em] 375:ml-[16.2em] 360:ml-[16.5em] ">
                  **Required
                </span>
              </label>
              <SearchInput
                placeholder="Search for Customer"
                data={customers.map((customer) => ({
                  ...customer,
                  display: `${customer.name} (${customer.phone})`,
                }))}
                displayKey="name"
                isLoading={isSearching}
                value={formData.customerId !== null ? formData.customerId : ''}
                selected={customer}
                onSelect={handleSetCustomer}
                handleChangeText={handleCustomerSearch}
                emptyMessage={'This customer is not found'}
                clearIcon={CloseIcon}
                clearIconClass="xl:mr-[1em] mr-[0.5em] md:mr-[3.2em] biglg:mr-[2em]"
                classNames="w-[350px]  md:w-[320px] ml-[-3em] border border-gray-300 bg-white max-h-[48px] min-h-[48px] placeholder:text-[15.25px] rounded  focus:outline-none focus:shadow-outline appearance-none focus:ring-0 leading-normal md:ml-[-1.8em] lg:ml-[2em] xl:ml-[7em] xl:w-[434px]"
                error={validationResults.customerId || draftValidationResults.customerId}
                onClearError={() => {
                  setValidationResults((prevState) => ({ ...prevState, customerId: undefined }));
                  setDraftValidationResults((prevState) => ({ ...prevState, customerId: undefined }));
                }}
                dropdownClassName="w-[350px] md:w-[320px] ml-[-3em] md:ml-[-1.7em] lg:ml-[2em] xl:ml-[7em] xl:w-[434px]"
                dropdownStyle={{
                  maxHeight: '800px',
                  overflowY: 'auto',
                }}
              />
              <a
                href="#customer-modal"
                className="inline-flex items-center lg:text-[16px] text-[12px] text-[#044E97] font-medium mt-[-1rem] ml-[-3.9em] md:ml-[-2em] lg:ml-[2.7em] xl:ml-[7.2em] biglg:ml-[2.2em]"
                onClick={() => setShowNewCustomerModal(true)}
              >
                New Customer
                <PlusBoxIcon />
              </a>
              <div className="mt-[-0.5em] ml-[-2.8em] md:ml-[-1.6em] lg:ml-[2em] xl:mt-[-0.5em] xl:ml-[7em]">
                {(validationResults.customerId || draftValidationResults.customerId) && (
                  <span className="text-red-500">
                    {validationResults.customerId || draftValidationResults.customerId}
                  </span>
                )}
              </div>
            </div>
            <div className="mt-[13px] lg:mt-0 xl:mt-0 2xl:mt-0 biglg:mt-[0.3em] bigmd:mt-0 md:mt-0 ">
              <label htmlFor="invoiceNo" className="text-[12px] xl:text-[13px] biglg:mt-[em]  xl:mt-0 lg:mt-[-0.7em]">
                Invoice Number
              </label>
              <div className="xl:mt-[-0.3em] biglg:mt-[-0.5em] lg:mt-[-0.6em] bigmd:mt-[-0.4em] md:mt-[-0.4em]">
                <InputComponent
                  placeholder="Invoice Number"
                  type="text"
                  value={formData.invoiceNumber}
                  name="invoiceNo"
                  handleChange={handleInputChange}
                  className={`w-[350px] md:w-[320px] xl:w-[434px] rounded-[4px]  border-1 border-[#A4A4A4] placeholder:text-[#A4A4A4] text-sm font-medium bg-gray-100 appearance-none disabled:opacity-50 disabled:bg-gray-700  max-h-[48px] min-h-[48px]  
                      ${formData.invoiceNumber ? '' : 'opacity-50'}`}
                  disabled={true}
                  error={validationResults.invoiceNumber || draftValidationResults.invoiceNumber}
                  onClearError={() => {
                    setValidationResults((prevState) => ({
                      ...prevState,
                      invoiceNumber: undefined,
                    }));
                    setDraftValidationResults((prevState) => ({
                      ...prevState,
                      invoiceNumber: undefined,
                    }));
                  }}
                />
              </div>
            </div>
          </div>

          <div className="lg:grid lg:grid-cols-2 md:grid md:grid-cols-2  md:mt-[52px] xl:gap-[24px] lg:gap-[36px] bigmd:mt-[52px] lg:mt-[52px] biglg:gap-[22px] biglg:mt-[52px] xl:mt-[52px]">
            <div className="ml-[3em] lg:ml-0 xl:ml-0 2xl:ml-0 biglg:ml-0 bigmd:ml-0 md:ml-0 mt-[13px] lg:mt-0 xl:mt-0 2xl:mt-0 biglg:mt-0 bigmd:mt-0 md:mt-0">
              <label
                htmlFor="issueDate"
                className="block text-[12px] xl:ml-[8.9em] xl:text-[13px] ml-[-3.7em] md:ml-[-2.2em] biglg:ml-[2.5em] lg:ml-[2.9em]  375:ml-[-4em]"
                style={{ whiteSpace: 'nowrap' }}
              >
                Issue Date
                <span className="text-red-500 ml-[16em] md:ml-[16.5em] lg:ml-[16.8em] xl:ml-[23.5em] 375:ml-[19em] 360:ml-[18.6em]">
                  **Required
                </span>
              </label>
              <InputComponent
                type="date"
                className="w-[350px]  md:w-[320px] ml-[-3em] border border-gray-300 bg-white max-h-[48px] min-h-[48px] placeholder:text-[15.25px] rounded  focus:outline-none focus:shadow-outline appearance-none focus:ring-0 leading-normal md:ml-[-1.8em] lg:ml-[2em] xl:ml-[7em] xl:w-[434px]"
                placeholder="Issue Date"
                value={formData.issueDate}
                name="issueDate"
                handleChange={handleInputChange}
                error={validationResults.issueDate || draftValidationResults.issueDate}
                restrictFutureDates={true}
              />
            </div>
            <div className="mt-[13px] lg:mt-0 xl:mt-0 2xl:mt-0 biglg:mt-0 bigmd:mt-0 md:mt-0 ">
              <label htmlFor="invoiceDueAt" className="xl:text-[13px] text-[12px] xl:mt-0 lg:mt-[-0.7em]">
                Due Date
                <span className="text-red-500 ml-[17em] md:ml-[17.5em] bigmd:ml-[17em] lg:ml-[17.2em] xl:ml-[23.9em] 375:ml-[19.8em] 360:ml-[18.8em]">
                  **Required
                </span>
              </label>
              <div className="xl:mt-[-0.2em] biglg:mt-[-0.4em] lg:mt-[-0.4em] md:mt-[-0.3em]">
                <InputComponent
                  type="date"
                  className="w-[350px] md:w-[320px] xl:w-[434px] rounded-[4px] placeholder:text-[#A4A4A4] border-1 border-[#A4A4A4] max-h-[48px] min-h-[48px] "
                  placeholder="Due Date"
                  value={formData.invoiceDueAt}
                  name="invoiceDueAt"
                  handleChange={handleInputChange}
                  restrictPreviousDates={false}
                  onClearError={() => {
                    setValidationResults((prevState) => ({
                      ...prevState,
                      invoiceDueAt: undefined,
                    }));
                    setDraftValidationResults((prevState) => ({
                      ...prevState,
                      invoiceDueAt: undefined,
                    }));
                  }}
                />
                <div className="ml-[0.1em] md:ml-[0.1em] md:mt-[0.5em] biglg:ml-[0.2em] lg:ml-[0.5em] xl:ml-[0.5em] xl:mt-[em]">
                  {(validationResults.invoiceDueAt || draftValidationResults.invoiceDueAt) && (
                    <span className="text-red-500" style={{ whiteSpace: 'nowrap', padding: '0', lineHeight: '1.2' }}>
                      {validationResults.invoiceDueAt || draftValidationResults.invoiceDueAt}
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>
          <div className="lg:flex lg:justify-left md:flex md:justify-left md:mt-[36px] bigmd:mt-[32px] xl:gap-[24px] xl:mt-[32px] lg:gap-[36px] lg:mt-[32px]  biglg:gap-[22px] biglg:mt-[32px] xl:ml-[0.2em]">
            <div className="ml-[3em] lg:ml-0 xl:ml-0 2xl:ml-0 biglg:ml-0 bigmd:ml-0 md:ml-0 mt-[13px] lg:mt-[-5px] xl:mt-0 2xl:mt-0 biglg:mt-0 bigmd:mt-[-5px] md:mt-[-5px]">
              <label
                htmlFor="Currency"
                className="inline-flex text-[12px] xl:text-[13px] ml-[-3.4em] md:ml-[-2.5em] lg:ml-[2.8em] xl:ml-[8.5em] 375:ml-[-3.9em]"
              >
                Select Currency
                <div
                  className="relative hidden lg:block"
                  onMouseEnter={handleInfoCircleCurrencyHover}
                  onMouseLeave={handleInfoCircleCrrencyMouseLeave}
                >
                  {isMiniCardCurrencyVisible ? (
                    <div className="absolute z-10 ml-[-4em]">
                      <MiniCard
                        text1="Incase you have a foreign client ? specify the "
                        text2=" exchange rate in Naira for better record keeping."
                        icon={<InfoCircle />}
                      />
                    </div>
                  ) : (
                    <div className='mt-[0.3em]'>
                    <InfoCircle />
                    </div>
                  )}
                </div>
              </label>
              <DropdownComponent
                name="currency"
                options={dropDownCurrency}
                value={formData.currency}
                placeholder="Select Currency"
                selectedValue={selectedCurrencyLabel}
                onChange={(name, value) => handleDropDownChange(name, value)}
                dropdownClassName="w-[350px]  md:w-[320px] ml-[-3em] border border-gray-300 bg-white md:ml-[-1.8em] lg:ml-[2em] xl:ml-[6.7em] xl:w-[434px]"
                iconClassName="mr-[4em] md:mr-[1em] xl:ml-[-1.5em] lg:mr-[1.1em] biglg:mr-[1em] 375:mr-[1em] bigmd:mr-[1em]"
              />
              <div className="ml-[-3em] md:ml-[-2em] bigmd:ml-[-1.7em] lg:ml-[2.2em] xl:ml-[7em] ">
                {(validationResults.currency || draftValidationResults.currency) && (
                  <span className="text-red-500" style={{ whiteSpace: 'nowrap', padding: '0', lineHeight: '1.2' }}>
                    {validationResults.currency || draftValidationResults.currency}
                  </span>
                )}
              </div>
            </div>
            {showRateInput && (
              <>
                <label
                  htmlFor="exchangeRate"
                  className="xl:text-[13px] text-[12px] lg:ml-[-1.5em] xl:ml-[-0.5em] biglg:ml-[0.3em] bigmd:ml-[3.5em] md:ml-[3.5em] 2xl:ml-[0.5em]"
                  style={{ whiteSpace: 'nowrap' }}
                >
                  Enter Rate
                </label>
                <div className="xl:mt-[1.2em] biglg:mt-[1.2em] lg:mt-[0.9em] bigmd:mt-[0.8em] md:mt-[0.8em] ">
                  <InputComponent
                    type="number"
                    value={formData.exchangeRate !== 0 ? formData.exchangeRate : ''}
                    name="exchangeRate"
                    placeholder="Exchange Rate"
                    handleChange={(e) => setFormData({ ...formData, exchangeRate: parseFloat(e.target.value) || 0 })}
                    className="w-[350px] md:w-[320px] xl:ml-[-5.4em] xl:w-[434px] biglg:ml-[-5em] lg:ml-[-6em] bigmd:ml-[-3.8em] md:ml-[-3.7em]"
                  />
                </div>
              </>
            )}
          </div>
        </div>
      </form>

      <CreateCustomer
        show={showNewCustomerModal}
        onClose={() => setShowNewCustomerModal(false)}
        onCustomerCreate={handleSetCustomer}
      />
    </div>
  );
};

export default InvoiceBasicInfo;
